import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, CircularProgress, Divider } from "@mui/material";
import { getEventDetails } from "@/controllers/events";
import { themeState } from "@/lib/store";
import { useRecoilValue } from "recoil";
import { EventsCallDetails } from "@/models/events";
import dayjs from "dayjs";
import { dateParser } from "@/lib/utils";
import SnackBar from "../snack-bar/SnackBar";
import ShareLinkButton from "@/components/share-link-button/ShareLinkButton";
import useModalLink, { openModalLink } from "src/hooks/useModalLink";

interface CallDetailsModalProps {
    open: boolean;
    onClose: () => void;
    eventId: number | null;
    eventTitle?: string;
    /** Adds the share button: the dialog is deep-linked and the URL is its link. */
    shareable?: boolean;
    /** Details already loaded (by a link's loader): shown as they are, without a second fetch. */
    details?: EventsCallDetails | null;
}

const CallDetailsModal: React.FC<CallDetailsModalProps> = ({ open, onClose, eventId, eventTitle = "Event Details", shareable, details }) => {
    const theme = useRecoilValue(themeState);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [callDetails, setCallDetails] = useState<EventsCallDetails | null>(null);
    const [periodEndDate, setPeriodEndDate] = useState<string | null>(null);
    const [snackBarOpen, setSnackBarOpen] = useState(false);

    useEffect(() => {
        // A new event (or Back closing the link) never shows the previous event's details.
        setCallDetails(null);
        setPeriodEndDate(null);
        setError(null);
        if (!open) return;
        if (details) showDetails(details);
        else if (eventId) fetchCallDetails();
    }, [open, eventId, details]);

    const showDetails = (data: EventsCallDetails) => {
        setCallDetails(data);
        const periodEndDateFormatted = data.periodEndDate ? dateParser(dayjs.utc(data.periodEndDate), "HH:mm", undefined, false, true) : null;
        periodEndDateFormatted !== "Invalid Date" && setPeriodEndDate(periodEndDateFormatted);
    };

    useEffect(() => {
        if (error) {
            setSnackBarOpen(true);
        }
    }, [error]);

    const fetchCallDetails = async () => {
        if (!eventId) return;

        setLoading(true);
        setError(null);
        setSnackBarOpen(false);
        try {
            const data = await getEventDetails(eventId);
            if (data.status === "error") {
                setError("Failed to load call details. Please try again.");
                return;
            }

            showDetails(data);
        } catch {
            setError("Failed to load call details. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
        setCallDetails(null);
        setPeriodEndDate(null);
        setError(null);
        setSnackBarOpen(false);
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            );
        }

        return (
            <Box>
                {callDetails && (
                    <>
                        <Typography variant="h6" gutterBottom>
                            {callDetails.conferenceTitle || eventTitle}
                        </Typography>
                        {periodEndDate && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Period end date:</strong> {periodEndDate}
                                </Typography>
                            </Box>
                        )}
                        {callDetails.country && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Country:</strong> {callDetails.country}
                                </Typography>
                            </Box>
                        )}
                        {callDetails.region && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Region:</strong> {callDetails.region}
                                </Typography>
                            </Box>
                        )}
                        {(callDetails.fiscalYear || callDetails.fiscalQuarter) && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Fiscal Period:</strong> {callDetails.fiscalYear && callDetails.fiscalQuarter ? `Q${callDetails.fiscalQuarter} ${callDetails.fiscalYear}` : callDetails.fiscalYear || `Q${callDetails.fiscalQuarter}`}
                                </Typography>
                            </Box>
                        )}
                        {callDetails.hostFirstname && callDetails.hostLastname && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                    Host
                                </Typography>
                                {callDetails.hostFirstname && callDetails.hostLastname && (
                                    <Box sx={{ mb: 1 }}>
                                        <Typography variant="body2">
                                            <strong>
                                                {callDetails.hostFirstname} {callDetails.hostLastname}
                                            </strong>
                                        </Typography>
                                        {callDetails.hostJobtitle && (
                                            <Typography variant="body2" color="text.secondary">
                                                {callDetails.hostJobtitle}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </>
                        )}
                        {callDetails.description && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                    Agenda
                                </Typography>
                                {callDetails.description && (
                                    <Box sx={{ mb: 1 }}>
                                        <Typography variant="body2">
                                            <strong>{callDetails.description}</strong>
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}
                        {callDetails.phone && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                    Dial-in Information
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    <strong>Phone:</strong> {callDetails.phone}
                                </Typography>
                                {callDetails.phoneDesc && (
                                    <Typography variant="body2" paragraph>
                                        <strong>Phone Description:</strong> {callDetails.phoneDesc}
                                    </Typography>
                                )}
                                {callDetails.tollFree && (
                                    <Typography variant="body2" paragraph>
                                        <strong>Toll Free:</strong> Yes
                                    </Typography>
                                )}
                                {callDetails.passcode && (
                                    <Typography variant="body2" paragraph>
                                        <strong>Access Code:</strong> {callDetails.passcode}
                                    </Typography>
                                )}
                                {callDetails.liveUrl && (
                                    <Typography variant="body2" paragraph>
                                        <strong>Webcast:</strong>{" "}
                                        <a href={callDetails.liveUrl} target="_blank" rel="noopener noreferrer" style={{ color: theme === "dark" ? "#90caf9" : "#1976d2" }}>
                                            {callDetails.liveUrl}
                                        </a>
                                    </Typography>
                                )}
                                {callDetails.replayUrl && callDetails.replayUrl !== "This replay is no longer available." && (
                                    <Typography variant="body2" paragraph>
                                        <strong>Replay:</strong>{" "}
                                        <a href={callDetails.replayUrl} target="_blank" rel="noopener noreferrer" style={{ color: theme === "dark" ? "#90caf9" : "#1976d2" }}>
                                            {callDetails.replayUrl}
                                        </a>
                                    </Typography>
                                )}
                                {(callDetails.replayStart || callDetails.replayEnd) && (
                                    <Typography variant="body2" paragraph>
                                        <strong>Replay Available:</strong>{" "}
                                        {callDetails.replayStart && callDetails.replayEnd
                                            ? `${dateParser(dayjs.utc(callDetails.replayStart), "D MMM YYYY [at] HH:mm", undefined, false, true)} - ${dateParser(dayjs.utc(callDetails.replayEnd), "D MMM YYYY [at] HH:mm", undefined, false, true)}`
                                            : dateParser(dayjs.utc(callDetails.replayStart), "D MMM YYYY [at] HH:mm", undefined, false, true) || dateParser(dayjs.utc(callDetails.replayEnd), "D MMM YYYY [at] HH:mm", undefined, false, true)}
                                    </Typography>
                                )}
                            </>
                        )}
                    </>
                )}
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: "400px",
                },
            }}
        >
            <DialogTitle>
                <Typography variant="h6">Call Details</Typography>
            </DialogTitle>
            <DialogContent>{renderContent()}</DialogContent>
            <DialogActions>
                {shareable && callDetails && <ShareLinkButton title={callDetails.conferenceTitle || eventTitle} size="medium" />}
                <Button onClick={handleClose} color="primary">
                    Close
                </Button>
            </DialogActions>
            <SnackBar open={snackBarOpen} setOpen={setSnackBarOpen} severity="error" text={error || ""} />
        </Dialog>
    );
};

type LinkedCall = { eventId: number; details?: EventsCallDetails };

/**
 * Opens an event's call details in the app-wide dialog, with its link (?show=call&id=<event id>) in the URL.
 * A click opens the dialog at once, which then fetches with its spinner and error snackbar.
 */
export const openCallDetails = (eventId: number | string) => openModalLink("call", eventId, { eventId: Number(eventId) } as LinkedCall);

/**
 * A shared link's call details, fetched before the dialog opens, so a dead link (bad id, 4xx error payload, which
 * resolves under validateStatus < 500) is dropped silently instead of opening an empty dialog.
 */
const loadCallDetails = async (id: string, signal: AbortSignal): Promise<LinkedCall> => {
    if (!/^\d+$/.test(id)) throw new Error("bad id");
    const data = await getEventDetails(Number(id), signal);
    const fields = Object.keys(data || {}).filter((key) => key !== "status" && key !== "detail");
    if (!data || data.status === "error" || "detail" in (data as object) || !fields.length) throw new Error("no call details");
    return { eventId: Number(id), details: data };
};

/** The call details dialog every events table opens, mounted once in _app so a link to it works on any page. */
export const CallDetailsHost = () => {
    const call = useModalLink<LinkedCall>("call", loadCallDetails, { global: true });
    return <CallDetailsModal open={!!call.item} onClose={call.close} eventId={call.item?.eventId ?? null} details={call.item?.details} eventTitle="Call details" shareable={call.linked} />;
};

export default CallDetailsModal;
